/**
 * web/src/screens/Notebook/types.ts — the Notebook screen's shared types,
 * constants and pure helpers (card vncVrGk1hc9i: moved out of index.tsx
 * unchanged; every declaration is now exported for the section modules).
 */
import { NOTEBOOK_THEME_LABELS } from '../../../../shared/types';
import { type Member } from '../../lib/api';
import { dateCostLabel } from '../../lib/dateIdeas';

/** 'trailer' is not a category (card 3ThoVHSXNpjo): a trailer is filed under
 *  the thing it trails — TV or a movie — so it has no chip of its own. */
export type Category = 'movie' | 'tv' | 'music' | 'podcast' | 'video' | 'article' | 'other';
export type Intent = 'play' | 'stream' | 'read' | 'open';
/** Notebook v2 typed collections (design v1.1 §5.3). */
export type EntryType = 'link' | 'gift' | 'memory' | 'date' | 'note' | 'idea';

/** Card 2qBAZOM2LFWa — one candidate from the card-detail place search. Names
 *  and addresses only: the worker strips the coordinates before the response
 *  leaves it, so nothing here can carry geo to the browser. */
export interface PlacePick { id: string; name: string; address: string | null; types: string[] }

/** Consumed on mount: the compose menu deep-links here to open the right
 *  composer (design v1.1 §5.2 — the + menu is feature discovery).
 *  Card RvESgw7lVASX widened this from note|idea: Memory and Date idea are
 *  quick-add composers of their own, and Link lands on the paste form. */
export interface NotebookComposeIntent {
  kind: 'note' | 'idea' | 'memory' | 'date' | 'link';
}

/** Mood / theme / time tags — the scan proposes them, you apply them. */
export type TagKind = 'mood' | 'theme' | 'time';
export const TAG_KIND_LABEL: Record<TagKind, string> = { mood: 'Mood', theme: 'Theme', time: 'Time' };

/** Editorial wording for the theme keys (card CTVSTgXykQ0Y) — one vocabulary
 *  for the whole app now (card BS0NIMpbbuC5): the digest's source list reads
 *  the same map, so the notebook card and the digest can never disagree about
 *  what a theme is called. The stored tag, the filter key, the API payload and
 *  every data-* attribute keep the RAW key (shared/types.ts
 *  NOTEBOOK_THEME_VOCAB); keys missing from the map fall back to the raw key —
 *  never blank. */
export const THEME_LABEL: Record<string, string> = NOTEBOOK_THEME_LABELS;

/** Display-only render of a tag: themes get the editorial line above, mood
 *  and time tags (free text, never taxonomy) print as typed. */
export function tagText(kind: TagKind, tag: string): string {
  return kind === 'theme' ? THEME_LABEL[tag] ?? tag : tag;
}

export interface NotebookTag {
  id: string;
  itemId: string;
  kind: TagKind;
  tag: string;
  source: 'human' | 'agent';
  addedBy: string | null;
  createdAt: number;
}

export interface Suggestion {
  id: string;
  itemId: string;
  kind: TagKind;
  tag: string;
  rationale: string;
  /** Kept only when it matches a partner's interests profile (card 0OvyJbpnACMX). */
  subject: string | null;
  status: 'pending';
  scanAt: number;
}

export interface NotebookItem {
  itemId: string;
  url: string;
  title: string | null;
  description: string | null;
  imageUrl: string | null;
  durationSeconds: number | null;
  provider: string;
  category: Category;
  categoryReason: string;
  categorizedBy: string | null;
  intent: Intent;
  shared: boolean;
  doneAt: number | null;
  addedBy: string;
  createdAt: number;
  updatedAt: number;
  entryType: EntryType;
  body: string | null;
  /** Gift enrichment state (card OviOTaNsNivQ): '' = not a gift, 'pending'
   *  = the search is running, 'done' = detail landed, 'off' = concierge off,
   *  'none' = the search came back empty. */
  enrichStatus: '' | 'pending' | 'done' | 'off' | 'none';
  enrichment: GiftDetail | null;
  /** Links v1 (card 4CXp7HfSIAGd): the kind the card filed under, so the row can
   *  name its home ("Gift ideas") instead of leaving the filing invisible. */
  kind: string;
  subtype: string | null;
  /** Cascade band + ranked candidates (sol review 2026-10-19, card
   *  xNGOA5rZz9LV): 'ask' is an UNRESOLVED card — the deterministic stages
   *  declined to file it, so the card carries the chooser instead of a
   *  filed-looking verdict. Cleared the moment a person answers. */
  kindTier: 'auto' | 'check' | 'ask' | null;
  kindOptions: { kind: string; subtype: string | null; confidence: number | null }[] | null;
  scope: 'shared' | 'just_me';
  /** Lifecycle facet (PRD §6.6) — the Saved / Planned / Done control reads it. */
  status: 'saved' | 'planned' | 'done' | 'passed';
  /** City or "near us" (PRD §5 layer 3); search covers it. */
  place: string | null;
  /** Card 2qBAZOM2LFWa — the day the memory happened, epoch ms. Sent by the
   *  detail form, rendered on the face as one line with the place. */
  eventDate: number | null;
}

/* ── PRD §6.5 E3/E4 — clusters ──────────────────────────────────────────────
   Two cards about the same place, or carrying the same theme, stop reading as
   two unrelated lines: they group themselves into ONE stacked card carrying a
   count ("Brighton Beach, VIC · 4 cards"), which expands in place and offers
   the pair's own actions — Plan this, Pick dates, View on map, Dissolve.

   The grouping is DERIVED on every render over `visible`, the list the server
   already scoped to this viewer (`listNotebook(viewerId)`). A partner's just-me
   card is not in that list, so it cannot enter a cluster this person sees; and
   because nothing is written back, one person's dissolve cannot hide a group
   from the other.

   What IS stored is each person's own opinion OF a group — dissolve / remove
   / add — in their localStorage under their member id, the same place the
   List/Grid pick keeps its own (card F5). A group needs >= 2 cards after those
   edits, so an emptied group simply stops rendering and "Show hidden groups"
   puts everything back. */

export interface ClusterPrefs {
  /** Cluster keys this person asked not to see. */
  dissolved: string[];
  /** `key::itemId` — a card pulled out of ONE group (a card stays a card). */
  excluded: string[];
  /** Cluster key -> card ids this person pulled into that group. */
  added: Record<string, string[]>;
}

export const EMPTY_CLUSTER_PREFS: ClusterPrefs = { dissolved: [], excluded: [], added: {} };

export const clusterPrefKey = (memberId: string | null) => `pear.nb.clusters.${memberId ?? 'anon'}`;
export const clusterExclKey = (clusterKey: string, itemId: string) => `${clusterKey}::${itemId}`;

export function readClusterPrefs(memberId: string | null): ClusterPrefs {
  if (typeof window === 'undefined') return EMPTY_CLUSTER_PREFS;
  try {
    const raw = window.localStorage.getItem(clusterPrefKey(memberId));
    if (!raw) return EMPTY_CLUSTER_PREFS;
    const p = JSON.parse(raw) as Partial<ClusterPrefs>;
    return {
      dissolved: Array.isArray(p.dissolved) ? p.dissolved.filter(x => typeof x === 'string') : [],
      excluded: Array.isArray(p.excluded) ? p.excluded.filter(x => typeof x === 'string') : [],
      added: p.added && typeof p.added === 'object' && !Array.isArray(p.added)
        ? Object.fromEntries(Object.entries(p.added as Record<string, unknown>)
            .filter(([, v]) => Array.isArray(v))
            .map(([k, v]) => [k, (v as unknown[]).filter((x): x is string => typeof x === 'string')]))
        : {},
    };
  } catch { return EMPTY_CLUSTER_PREFS; }
}

/** Group keys are case- and space-blind ("Brighton Beach, VIC" and
 *  "brighton  beach, vic" are one group); the LABEL keeps the first member's
 *  own casing, so the cluster names the place the way it was typed. */
export function clusterNorm(s: string): string {
  return s.trim().replace(/\s+/g, ' ').toLowerCase();
}

export interface FeedCluster {
  key: string;
  kind: 'place' | 'theme';
  label: string;
  members: NotebookItem[];
  /** Distinct places among the members — the "· 4 places" of PRD E3. */
  places: number;
}

export function clusterTitleOf(item: NotebookItem): string {
  return item.entryType === 'link'
    ? (item.title?.trim() || hostOf(item.url))
    : (item.body?.trim() || ENTRY_LABEL[item.entryType]);
}

export function deriveClusters(
  items: NotebookItem[],
  tags: NotebookTag[],
  prefs: ClusterPrefs,
): FeedCluster[] {
  const groups = new Map<string, { kind: FeedCluster['kind']; label: string; members: NotebookItem[] }>();
  const byId = new Map(items.map(i => [i.itemId, i]));

  const join = (key: string, kind: FeedCluster['kind'], label: string, item: NotebookItem) => {
    const g = groups.get(key) ?? { kind, label, members: [] };
    if (!g.members.some(m => m.itemId === item.itemId)) g.members.push(item);
    groups.set(key, g);
  };

  for (const item of items) {
    const place = (item.place ?? '').trim();
    if (place) join(`place:${clusterNorm(place)}`, 'place', place, item);
  }
  for (const t of tags) {
    if (t.kind !== 'theme') continue;
    const item = byId.get(t.itemId);
    if (item) join(`theme:${clusterNorm(t.tag)}`, 'theme', tagText('theme', t.tag), item);
  }
  /* E4 "add members" — a card pulled into a group it does not mechanically
     belong to. Only ever edits this person's own view of the feed. */
  for (const [key, ids] of Object.entries(prefs.added)) {
    const g = groups.get(key);
    if (!g) continue;
    for (const id of ids) {
      const item = byId.get(id);
      if (item && !g.members.some(m => m.itemId === id)) g.members.push(item);
    }
  }

  const dissolved = new Set(prefs.dissolved);
  const out: FeedCluster[] = [];
  for (const [key, g] of groups) {
    if (dissolved.has(key)) continue;
    const members = g.members.filter(m => !prefs.excluded.includes(clusterExclKey(key, m.itemId)));
    if (members.length < 2) continue;
    const places = new Set(members.map(m => (m.place ?? '').trim()).filter(Boolean));
    out.push({ key, kind: g.kind, label: g.label, members, places: places.size });
  }
  /* Concrete first — a resolved place outranks a theme — then the bigger
     groups, so a live list keeps a stable, most-useful-first order. */
  return out.sort((a, b) => {
    if (a.kind !== b.kind) return a.kind === 'place' ? -1 : 1;
    return b.members.length - a.members.length;
  });
}

/** Mirrors GiftEnrichment in shared/types.ts. */
export interface GiftDetail {
  source: 'serpapi' | 'apiserpent';
  query: string;
  priceBand: string;
  why: string;
  picks: { title: string; price: string | null; url: string; rating: string | null }[];
  count: number;
  at: number;
}

/** The Vault half of the Date Planner (card ZdYBVP203b8a) — the fields the
 *  pool list under the Date tile reads. Mirrors DateView in shared/types.ts;
 *  the web bundle types its own surface rather than importing shared. */
export interface DateIdeaRow {
  ideaId: string;
  title: string;
  note: string | null;
  source: string;
  mode: string;
  costBand: string;
  energy: string;
  provenance: string | null;
  state: string;
}

export const DATE_MODE_LABEL: Record<string, string> = { stay_in: 'In', go_out: 'Out', virtual: 'Virtual' };

export function dateMeta(d: DateIdeaRow): string {
  const bits: string[] = [];
  if (d.costBand) bits.push(dateCostLabel(d.costBand));
  if (d.mode) bits.push(DATE_MODE_LABEL[d.mode] ?? d.mode);
  return bits.join(' · ');
}

/** Display only (card CTVSTgXykQ0Y) — the chip, the retag prompt and the
 *  select all read these; the posted `category` stays the raw key above. */
export const CATEGORY_LABEL: Record<Category, string> = {
  movie: 'Movie', tv: 'TV', music: 'Music',
  podcast: 'Podcast', video: 'Video', article: 'Article', other: 'Something else',
};

export const INTENT_VERB: Record<Intent, string> = {
  play: 'Play', stream: 'Stream', read: 'Read', open: 'Open',
};

/** Founder email 2026-10-19: the done verb is context aware — a link you
 *  Watched, Listened to or Read says so; anything else is honestly "Done".
 *  Read straight off the card's own kind, so it can never disagree with the
 *  filing. */
export const KIND_DONE_VERB: Record<string, string> = {
  watch: 'Watched', listen: 'Listened', read: 'Read', play: 'Played',
  learn: 'Learned', make: 'Made',
};
export const doneVerb = (kind: string | null): string => (kind ? KIND_DONE_VERB[kind] ?? 'Done' : 'Done');

/** Sol review 2026-10-19: the correction control read the legacy taxonomy
 *  while the card headline read the kind — two vocabularies on one card.
 *  Each legacy option now names the filing it lands (linkFacets mapping). */
export const CATEGORY_TO_KIND: Record<Category, string> = {
  movie: 'watch', tv: 'watch', video: 'watch',
  music: 'listen', podcast: 'listen', article: 'read', other: 'other',
};

export function durationLabel(s: number | null): string | null {
  if (!s) return null;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}` : `${m}:${String(sec).padStart(2, '0')}`;
}

export function hostOf(u: string): string {
  try { return new URL(u).hostname.replace(/^www\./, ''); } catch { return u; }
}

/** PRD E1/E2 — one place string from a search candidate. Google's formatted
 *  address usually already leads with the place name, so prefixing it again
 *  put "Brighton Beach, Brighton Beach, VIC …" on the card; the name only
 *  leads when the address does not carry it. Both editors (the card's detail
 *  form and the panel's correction) build the string through here. */
export function placeFromPick(p: PlacePick): string {
  if (!p.address) return p.name;
  return p.address.toLowerCase().startsWith(p.name.toLowerCase()) ? p.address : `${p.name}, ${p.address}`;
}

/** Card FsDDh1AF6Ege — date-added on every notebook card, straight from the
 *  item's own record. Day + month + year, no clock time: "when we saved it"
 *  is a day-level fact. Same shape the chores timeline prints. */
export function addedLabel(at: number): string {
  const d = new Date(at);
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Card SApyMHlbeyhg — the note's byline: WHO saved it, and the moment they
 *  did, to the minute ("By Partner A, 16 October 2026 12:06 PM"). Unlike the
 *  day-level Added line above, attribution is a precise instant — the same
 *  note can be saved by either partner within one evening, and the byline is
 *  what tells them apart. */
export function bylineLabel(at: number): string {
  const d = new Date(at);
  const day = d.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `${day} ${time}`;
}

/** Resolve a note's author memberId to a readable name. "You" for the viewer's
 *  own row, the partner's display name otherwise, and a neutral fallback for a
 *  row whose author is not in the cached members list yet. */
export function bylineAuthor(addedBy: string | null, members: Member[], meId: string | null): string {
  if (!addedBy) return 'Someone';
  if (meId && addedBy === meId) return 'You';
  const m = members.find(x => x.memberId === addedBy);
  return m?.displayName?.trim() || 'Your partner';
}

/** The partner's display name for the visibility pill ("Shared with <name>").
 *  The other member of the couple — fallback "your partner" when the cache has
 *  only this member (fresh pair, before the members list fills). */
export function partnerNameOf(members: Member[], meId: string | null): string {
  const other = members.find(x => x.memberId !== meId);
  return other?.displayName?.trim() || 'your partner';
}

/** Notebook v2 collections (design v1.1 §5.3/§7) — quick-add types shown as
 *  quick tiles plus the link paste. 'all' shows everything.
 *
 *  The Date tile is the Vault half of the Date Planner (card ZdYBVP203b8a,
 *  PRD §3): Vault captures, Us → Dates plans, and both are the SAME
 *  DateIdea pipeline. So the tile is named for what it makes — a "Date
 *  idea" — and saving one posts to /api/dates, not /api/notebook. */
export const ENTRY_TILES: Array<{ key: EntryType | 'all'; label: string }> = [
  { key: 'all', label: 'Everything' },
  { key: 'link', label: 'Collection' },
  { key: 'gift', label: 'Gift' },
  { key: 'memory', label: 'Memory' },
  { key: 'date', label: 'Date idea' },
  { key: 'note', label: 'Note' },
  { key: 'idea', label: 'Idea' },
];

/** Card anKalYfYvokw (IA phase 2): the SIX things a couple can actually save.
 *  This is ENTRY_TILES with 'all' removed — the empty-state strip renders it,
 *  so the six collections a person can file into are stated on Home instead of
 *  being discoverable only by scrolling the tab row. Typed as `EntryType` on
 *  purpose: an invented eleventh tile ("places", "events") cannot be added here
 *  without also existing in the entry model. */
export const SAVABLE_TYPES = ENTRY_TILES.filter(
  (t): t is { key: EntryType; label: string } => t.key !== 'all',
);

/**
 * Per-tab id override for the collection row (UX item 6): the Note / Idea
 * tiles keep the ids the quick-add composer and the walks already target, so
 * the panel's `aria-labelledby` resolves to exactly the string the tab
 * renders. ONE function, handed to both SubTabs and SubPanel — two spellings
 * of the same pattern are two chances to drift apart.
 */
export const nbSubTabId = (key: string): string | undefined =>
  key === 'note' ? 'nb-tile-note' : key === 'idea' ? 'nb-tile-idea' : undefined;

export const ENTRY_LABEL: Record<EntryType, string> = {
  link: 'Collection', gift: 'Gift', memory: 'Memory', date: 'Date', note: 'Note', idea: 'Idea',
};

/** The types the capture bar's chip offers (PRD §6.2 C2). Link is missing on
 *  purpose: it is the one type the TEXT decides (a URL cannot be saved as
 *  words), so a URL capture shows "Link" and the menu offers everything it
 *  could be filed as instead. */
export const CAPTURE_TYPES: EntryType[] = ['gift', 'memory', 'date', 'note', 'idea'];

/** Card snBw8Ua7Wnmc — the noun the confirm sheet names ("Remove this
 *  memory?"). ENTRY_LABEL is title-case and calls a link a "Collection",
 *  which reads wrong inside a sentence, so the destroy path keeps its own
 *  lowercase vocabulary instead of reusing it. */
export const REMOVE_NOUN: Record<EntryType, string> = {
  link: 'link', gift: 'gift', memory: 'memory', date: 'date', note: 'note', idea: 'idea',
};

export const MEETING_LABEL = 'Meeting';
/** Plural forms — `ENTRY_LABEL + 's'` would render "Memorys save to…". */
export const ENTRY_PLURAL: Record<EntryType, string> = {
  link: 'Collection', gift: 'Gifts', memory: 'Memories', date: 'Dates', note: 'Notes', idea: 'Ideas',
};

/** Empty-state first-move line per collection (card 7VIbSBeJhvbQ). The old
 *  single line was link copy ("drop a trailer … an album"), so it named a
 *  link on the Gift / Memory / Date / Note / Idea tabs where a link cannot
 *  be added at all. `requestedCollection` is a plain string from the More
 *  menu, so anything unrecognised falls back to the Everything line rather
 *  than rendering `undefined`. */
export const EMPTY_HINT: Record<'all' | EntryType, string> = {
  all: 'Add a trailer you both want to see, or an album you keep coming back to.',
  link: 'Add a trailer you both want to see, or an album you keep coming back to.',
  gift: 'Add a gift idea — the concierge goes looking for suggestions.',
  memory: 'Add a memory — a moment you both want to keep.',
  date: 'Add a date idea — it waits here until you plan together.',
  note: 'Add a note — anything you both want on the record.',
  idea: 'Add an idea — a trip, a plan, a thing to do one day.',
};
